import { isArray, isString } from 'lodash'

import { formatCallDuration } from '@/utils/helpers'

import { CHANNEL_TYPE } from '@/utils/constants'
import {
  ANALYTICS_FILTER_TYPE,
  CALL_EXPLORER_FILTER_TYPE,
  SENTIMENT_FILTER_TYPE,
  COPILOT_FILTER_TYPE,
  INBOX_FILTER_TYPE,
  PROMPT_ENGINEERING_FILTER_TYPE,
  QA_ACTIVITY_FILTER_TYPE,
} from './savedFilters.constants'

export const getFilterAgents = (savedFilter) => {
  if (!savedFilter || !savedFilter?.filters) {
    return null
  }

  if (savedFilter.filters) {
    return savedFilter.filters?.agents
  }

  return null
}

// Take the existing filter fetched from the endpoint and convert it to the format that the form schema expects
export const formatSavedFilterFormExistingValues = (savedFilter = {}, filterType) => {
  return {
    name: savedFilter.name,
    isDefault: savedFilter.is_default,
    filterType: savedFilter.filter_type,

    // Inner values
    agents: savedFilter.filters.agents || [],
    playbooks: savedFilter.filters.playbooks || [],
    voipCampaigns: savedFilter.filters.voipCampaigns || [],
    tags: savedFilter.filters.tags || [],
    callDuration: savedFilter.filters.callDuration || '1',
    maxCallDuration: savedFilter.filters.maxCallDuration || '',
    dateRange: savedFilter.filters.dateRange || 'today',
    channel: savedFilter.filters.channel || CHANNEL_TYPE.CALL,

    // Reporting only
    ...(filterType === ANALYTICS_FILTER_TYPE && {
      includeManagers: savedFilter.filters.includeManagers || false,
    }),

    // Call Explorer only
    ...(filterType === CALL_EXPLORER_FILTER_TYPE && {
      dispositions: {
        includes: savedFilter.filters?.dispositions?.includes ?? true,
        selected: (savedFilter.filters?.dispositions?.selected || []).map((val) =>
          typeof val === 'string'
            ? { value: val, label: val }
            : {
                value: val?.value || '',
                label: val?.label || val?.value || '',
              }
        ),
      },
      isWin: savedFilter.filters.isWin,
      includeCallsWithoutAudio: savedFilter.filters.includeCallsWithoutAudio || false,
      keywords: isArray(savedFilter.filters.keywords)
        ? savedFilter.filters.keywords.map((keyword) => keyword.value).join(',')
        : [],
      keywordOptions: savedFilter.filters.keywordOptions || { side: 'both', logic: 'or' },
      checklist: savedFilter.filters.checklist || { selected: [], includes: true },
      deck: savedFilter.filters.deck || { selected: [], includes: true },
      postcall: savedFilter.filters.postcall || { selected: [], includes: true },
      notifications: savedFilter.filters.notifications || { selected: [], includes: true },
      scorecards: savedFilter.filters.scorecards,
      scoredStatus: savedFilter.filters.scoredStatus || '',
      scorecardThreshold: savedFilter.filters.scorecardThreshold || '',
      voipCustomerIds: Array.isArray(savedFilter.filters?.voipCustomerIds)
        ? savedFilter.filters.voipCustomerIds
            .map((voipCustomerId) => voipCustomerId.value)
            .join(',')
        : [],
      endingSentiment: Array.isArray(savedFilter.filters?.endingSentiment)
        ? savedFilter.filters.endingSentiment
        : [],
      attachments: Array.isArray(savedFilter.filters?.attachments)
        ? savedFilter.filters.attachments
        : [],
      direction: savedFilter.filters?.direction || '',
      minMessageCount: savedFilter.filters.minMessageCount || '',
      maxMessageCount: savedFilter.filters.maxMessageCount || '',
      agentType: savedFilter.filters.agentType === 'All' ? '' : savedFilter.filters.agentType || '', // backwards compatibility with 'All'
    }),

    // Copilot only
    ...(filterType === COPILOT_FILTER_TYPE && {
      agents: savedFilter.filters.agents || [],
      playbooks: savedFilter.filters.playbooks || [],
      playlists: savedFilter.filters.playlists || [],
      voipCampaigns: savedFilter.filters.voipCampaigns || [],
      tags: savedFilter.filters.tags || [],
      scorecards: savedFilter.filters.scorecards || [],
      isWin: savedFilter.filters.isWin,
      scoredStatus: savedFilter.filters.scoredStatus,
      dateRange: savedFilter.filters.dateRange || 'today',
      dispositions: {
        includes: savedFilter.filters?.dispositions?.includes ?? true,
        selected: (savedFilter.filters?.dispositions?.selected || []).map((val) =>
          typeof val === 'string'
            ? { value: val, label: val }
            : {
                value: val?.value || '',
                label: val?.label || val?.value || '',
              }
        ),
      },
    }),

    // Prompt Engineering only
    ...(filterType === PROMPT_ENGINEERING_FILTER_TYPE && {
      organizations: savedFilter.filters.organizations || [],
      scorecards: savedFilter.filters.scorecards || [],
    }),

    // QA Activity only
    ...(filterType === QA_ACTIVITY_FILTER_TYPE && {
      agents: savedFilter.filters.agents || [],
      scorecards: savedFilter.filters.scorecards || [],
      playlists: savedFilter.filters.playlists || [],
      dateRange: savedFilter.filters.dateRange || 'today',
    }),
  }
}

export const getSavedFilterFormDefaultValues = (filterType, selectedChannel = null) => {
  return {
    isDefault: false,
    filterType,
    channel: selectedChannel || CHANNEL_TYPE.CALL,
    ...([ANALYTICS_FILTER_TYPE, CALL_EXPLORER_FILTER_TYPE].includes(filterType) && {
      callDuration: '1',
      maxCallDuration: '',
      dateRange: 'today',
    }),
    ...(filterType === QA_ACTIVITY_FILTER_TYPE && {
      dateRange: 'today',
    }),
    ...(filterType === CALL_EXPLORER_FILTER_TYPE && {
      keywordOptions: { side: 'both', logic: 'or' },
      minMessageCount: '',
      maxMessageCount: '',
    }),
    ...(filterType === COPILOT_FILTER_TYPE && {
      dateRange: 'today',
      isWin: undefined,
    }),
  }
}

// Helper to convert comma-separated string or array to [{ label, value }]
function toLabelValueArray(input) {
  if (isString(input)) {
    return input
      .split(',')
      .map((item) => item.trim())
      .map((item) => ({ label: item, value: item }))
      .filter((item) => item.value)
  }
  return input || []
}

// Format the filters from the form into the format that the API expects
// `is_default` is a boolean that determines which filter is the default for a filterType.
// Unrelated, the analytics filter type is a string called 'default'.
// Need to manually define all these so nothing extra gets through
// This can probbaly be refactored but I'm not sure the best way to do it.
export const formatSavedFilters = (savedFilter = {}, organizationId, filterType) => ({
  name: savedFilter.name,
  is_default: savedFilter.isDefault,
  filter_type: filterType || savedFilter.filterType,
  filters: {
    channel: savedFilter.channel || CHANNEL_TYPE.CALL,

    // Sentiment only
    ...(filterType === SENTIMENT_FILTER_TYPE && {
      ignoreWeekends: savedFilter.ignoreWeekends,
      timePeriod: savedFilter.timePeriod,
    }),

    // Inbox, Reporting and Call Explorer
    ...([
      ANALYTICS_FILTER_TYPE,
      CALL_EXPLORER_FILTER_TYPE,
      INBOX_FILTER_TYPE,
      COPILOT_FILTER_TYPE,
    ].includes(filterType) && {
      organization: savedFilter.organization || organizationId,
      ...(savedFilter.organizationId && { organizationId: savedFilter.organizationId }), // new Global Filtrs component will use organizationId
      agents: savedFilter.agents,
      tags: savedFilter.tags,
    }),

    // Reporting and Call Explorer
    ...([ANALYTICS_FILTER_TYPE, CALL_EXPLORER_FILTER_TYPE].includes(filterType) && {
      playbooks: savedFilter.playbooks,
      callDuration: formatCallDuration(savedFilter.callDuration),
      maxCallDuration: savedFilter.maxCallDuration,
      dateRange: savedFilter.dateRange,
    }),

    // Reporting only
    ...(filterType === ANALYTICS_FILTER_TYPE && {
      includeManagers: savedFilter.includeManagers,
    }),

    // Call Explorer only
    ...(filterType === CALL_EXPLORER_FILTER_TYPE && {
      dispositions: {
        includes:
          typeof savedFilter.dispositions?.includes === 'boolean'
            ? savedFilter.dispositions.includes
            : true,
        selected: savedFilter.dispositions?.selected ?? [],
      },
      isWin: savedFilter.isWin, // TODO: need to add to reporting
      includeCallsWithoutAudio: savedFilter.includeCallsWithoutAudio,
      voipCampaigns: savedFilter.voipCampaigns,
      keywords: toLabelValueArray(savedFilter.keywords),
      keywordOptions: savedFilter.keywordOptions,
      checklist: savedFilter.checklist,
      deck: savedFilter.deck,
      postcall: savedFilter.postcall,
      notifications: savedFilter.notifications,
      scorecards: savedFilter.scorecards,
      scoredStatus: savedFilter.scoredStatus,
      scorecardThreshold: savedFilter.scorecardThreshold,
      voipCustomerIds: toLabelValueArray(savedFilter.voipCustomerIds),
      endingSentiment: savedFilter.endingSentiment,
      attachments: savedFilter.attachments,
      direction: savedFilter.direction,
      minMessageCount: savedFilter.minMessageCount,
      maxMessageCount: savedFilter.maxMessageCount,
      agentType: savedFilter.agentType === 'All' ? '' : savedFilter.agentType || '',
    }),

    // Copilot only
    ...(filterType === COPILOT_FILTER_TYPE && {
      agents: savedFilter.agents,
      playbooks: savedFilter.playbooks,
      playlists: savedFilter.playlists,
      voipCampaigns: savedFilter.voipCampaigns,
      scorecards: savedFilter.scorecards,
      tags: savedFilter.tags,
      isWin: savedFilter.isWin,
      scoredStatus: savedFilter.scoredStatus,
      dateRange: savedFilter.dateRange,
      dispositions: {
        includes:
          typeof savedFilter.dispositions?.includes === 'boolean'
            ? savedFilter.dispositions.includes
            : true,
        selected: Array.isArray(savedFilter.dispositions)
          ? savedFilter.dispositions
          : savedFilter.dispositions?.selected ?? [],
      },
    }),

    // Prompt Engineering only
    ...(filterType === PROMPT_ENGINEERING_FILTER_TYPE && {
      organizations: savedFilter.organizations,
      scorecards: savedFilter.scorecards,
    }),

    // QA Activity only
    ...(filterType === QA_ACTIVITY_FILTER_TYPE && {
      organization: savedFilter.organization || organizationId,
      agents: savedFilter.agents,
      scorecards: savedFilter.scorecards,
      playlists: savedFilter.playlists,
      dateRange: savedFilter.dateRange,
    }),
  },
})
